<template>
  <div class="onboarding-parent">
    <header v-if="!isLSCustAppWebView" class="onboarding-parent--header">
      <img
        class="onboarding-parent--wordmark"
        :src="isLawnStarter ? lsLogo : ''"
        :alt="brandName"
      />
    </header>
    <div class="container">
      <div class="columns">
        <div
          v-if="highlightedStep <= 3"
          class="column onboarding-parent--steps"
          :class="{
            'onboarding-parent--steps-simplified': shouldRenderSimplifiedWalkthrough,
          }"
        >
          <div
            :class="{
              'onboarding-parent--steps-highlighted': highlightedStep === 1,
              'onboarding-parent--simplified': shouldRenderSimplifiedWalkthrough,
            }"
          >
            <span v-if="screenSize.desktop">1. Services</span>
            <span v-else>
              <p>1</p>
              <p>Services</p>
            </span>
          </div>
          <div v-if="!screenSize.desktop" class="onboarding-parent--steps-line"></div>
          <div
            :class="{
              'onboarding-parent--steps-highlighted': highlightedStep === 2,
              'onboarding-parent--simplified': shouldRenderSimplifiedWalkthrough,
            }"
          >
            <span v-if="screenSize.desktop">2. Property Details</span>
            <span v-else>
              <p>2</p>
              <p>Property Details</p>
            </span>
          </div>
          <div
            v-if="!screenSize.desktop && !processingOrder.policies_accepted_at"
            class="onboarding-parent--steps-line"
          ></div>
          <div
            v-if="!processingOrder.policies_accepted_at"
            :class="{
              'onboarding-parent--steps-highlighted': highlightedStep === 3,
            }"
          >
            <span v-if="screenSize.desktop">3. Policies</span>
            <span v-else>
              <p>3</p>
              <p>Policies</p>
            </span>
          </div>
        </div>
      </div>
      <!-- key needed here to stop Vue from being so smart and not replacing elements between steps -->
      <RouterView :key="$route.fullPath" />
    </div>
  </div>
</template>

<script>
import { lsRouterHelpers } from 'ls-vue-common';
import { mapState, mapActions, mapGetters } from 'vuex';
import { AUTH__PROCESSING_ORDER, FEATURE_FLAGS__REGISTER_EXPOSURE } from '@/store/types';
import { themedComponentMixin } from '@/mixins';
import LsLogo from '@/assets/lawnstarter.svg';

export default {
  name: 'OnboardingParentView',
  mixins: [themedComponentMixin],
  data() {
    return {
      lsLogo: LsLogo,
      name: lsRouterHelpers.getName(this.$route),
      isLSCustAppWebView: window.isLSCustAppWebView,
    };
  },
  computed: {
    ...mapGetters({
      processingOrder: [AUTH__PROCESSING_ORDER],
    }),
    ...mapState(['screenSize']),
    highlightedStep() {
      const steps = {
        onboardingPricedServices: 1,
        onboardingServices: 1,
        onboardingPropertyInfo: 2,
        onboardingPolicies: 3,
        onboardingDistributeEarly: 4,
        onboardingWelcomeShare: 4,
      };
      return steps[this.name];
    },
    shouldRenderSimplifiedWalkthrough() {
      return this.processingOrder.policies_accepted_at !== null;
    },
  },
  watch: {
    $route() {
      this.name = lsRouterHelpers.getName(this.$route);
    },
  },
  created() {
    this[FEATURE_FLAGS__REGISTER_EXPOSURE]({ flag: 'orders.customers.distribute_early' });
  },
  methods: {
    ...mapActions([FEATURE_FLAGS__REGISTER_EXPOSURE]),
  },
};
</script>

<style lang="scss" scoped>
.onboarding-parent {
  margin-bottom: 30px;
}

.onboarding-parent--header {
  height: $navbar-height;
  background: $primary;
  margin-bottom: 66px;
  display: flex;
  align-items: center;
  padding: 0 34px;
}

.onboarding-parent--wordmark {
  width: 172px;
  height: 22px;
}

.onboarding-parent--steps-simplified {
  flex-direction: row;
  justify-content: center !important;
}

.onboarding-parent--steps {
  border-bottom: 1px solid $primary-light;
  display: flex;
  padding-bottom: 34px;
  margin-bottom: 34px;

  div {
    &:not(:last-of-type) {
      margin-right: 34px;
    }

    span {
      font-weight: bold;
      font-size: $size-1;
      color: $primary-light;
    }
  }
}

.onboarding-parent--steps-highlighted span {
  color: $primary !important;
}

.onboarding-parent--steps-line {
  height: 50px;
  width: 1px;
  background: $primary-light;
}

.onboarding-parent--simplified {
  padding-left: 25px;
  padding-right: 25px;
}

@media (max-width: $mobile-max-width) {
  .onboarding-parent--header {
    justify-content: center;
    margin-bottom: 24px;
  }

  .onboarding-parent--steps {
    justify-content: space-between;
    padding-bottom: 24px;
    margin-bottom: 24px;

    div {
      &:not(:last-of-type) {
        margin-right: 0;
      }

      span {
        text-align: center;
        p {
          &:first-of-type {
            font-size: $size-4;
          }
          &:last-of-type {
            font-size: $size-6;
          }
        }
      }
    }
  }
}
.themed-app {
  .onboarding-parent--header {
    background: $theme-color-primary;
  }
  .onboarding-parent--steps {
    border-color: $theme-color-primary-light;
    div {
      span {
        font-size: $theme-txt-headline;
        color: $theme-color-primary-light;
      }
    }
  }

  .onboarding-parent--steps-highlighted span {
    color: $theme-color-primary !important;
  }

  .onboarding-parent--steps-line {
    background: $theme-color-primary-light;
  }

  .onboarding-parent--wordmark {
    content: $theme-logo-white;
    height: 21px;
    width: calc(21px * #{$theme-logo-ratio});
  }

  @media (max-width: $mobile-max-width) {
    .onboarding-parent--steps {
      div {
        span {
          p {
            &:first-of-type {
              font-size: $theme-txt-body-lg;
            }
            &:last-of-type {
              font-size: $theme-txt-body-sm;
            }
          }
        }
      }
    }
  }
}
</style>
